import React from "react";
import { useTranslation } from "react-i18next";
import { StepComponentProps } from "../../components/MultiStepGradeModal";

interface ChoiceBoardAdvancedData {
  preferredLearningStyles?: string[];
  difficultyDistribution?: string;
  selectionRequirements?: string;
  learningOutcomesFocus?: string[];
}

export const SelectionRequirementsStep: React.FC<
  StepComponentProps<ChoiceBoardAdvancedData>
> = ({ data, onDataChange, themeColor }) => {
  const { t } = useTranslation();

  const predefinedRequirements = [
    {
      id: "standard",
      emoji: "📋",
      label: t("choiceBoards.selectionRequirements.standard"),
      desc: t("choiceBoards.selectionRequirements.standardDesc"),
      value: t("choiceBoards.selectionRequirements.standard"),
    },
    {
      id: "flexible",
      emoji: "🔄",
      label: t("choiceBoards.selectionRequirements.flexible"),
      desc: t("choiceBoards.selectionRequirements.flexibleDesc"),
      value: t("choiceBoards.selectionRequirements.flexible"),
    },
    {
      id: "structured",
      emoji: "🏗️",
      label: t("choiceBoards.selectionRequirements.structured"),
      desc: t("choiceBoards.selectionRequirements.structuredDesc"),
      value: t("choiceBoards.selectionRequirements.structured"),
    },
  ];

  const handleRequirementSelect = (value: string) => {
    onDataChange({ selectionRequirements: value });
  };

  const handleCustomRequirement = (value: string) => {
    onDataChange({ selectionRequirements: value });
  };

  return (
    <div className="space-y-8">
      <div className="text-center mb-6">
        <h2
          className={`text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-${themeColor}-600 to-${themeColor}-800 dark:from-${themeColor}-400 dark:to-${themeColor}-600`}
        >
          {t("choiceBoards.steps.selectionRequirements.title")}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t("choiceBoards.steps.selectionRequirements.description")}
        </p>
      </div>

      {/* Predefined Requirements */}
      <div className="space-y-4">
        <label
          className={`block text-sm font-medium text-${themeColor}-700 dark:text-${themeColor}-300`}
        >
          {t("choiceBoards.form.selectionRequirements")}
        </label>

        <div className="space-y-3">
          {predefinedRequirements.map((requirement) => (
            <div
              key={requirement.id}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                data.selectionRequirements === requirement.value
                  ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
              }`}
              onClick={() => handleRequirementSelect(requirement.value)}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                    data.selectionRequirements === requirement.value
                      ? `border-${themeColor}-500 bg-${themeColor}-500`
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                >
                  {data.selectionRequirements === requirement.value && (
                    <div className="w-2 h-2 rounded-full bg-white"></div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{requirement.emoji}</span>
                    <h3 className="font-medium text-base">
                      {requirement.label}
                    </h3>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {requirement.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Custom Requirements */}
      <div className="space-y-4">
        <label
          className={`block text-sm font-medium text-${themeColor}-700 dark:text-${themeColor}-300`}
        >
          {t("choiceBoards.form.customRequirements")}
        </label>
        <textarea
          value={
            predefinedRequirements.some(
              (r) => r.value === data.selectionRequirements,
            )
              ? ""
              : data.selectionRequirements || ""
          }
          onChange={(e) => handleCustomRequirement(e.target.value)}
          placeholder={t("choiceBoards.form.customRequirementsPlaceholder")}
          className={`w-full rounded-xl border-2 border-gray-200 dark:border-gray-700 p-4 text-base focus:ring-2 focus:ring-${themeColor}-500 focus:border-transparent dark:bg-gray-700/50 transition-all duration-200 min-h-[100px]`}
          onClick={() => {
            if (
              predefinedRequirements.some(
                (r) => r.value === data.selectionRequirements,
              )
            ) {
              handleCustomRequirement("");
            }
          }}
        />
      </div>
    </div>
  );
};
